import apiService from "@/lib/api";
import { messageForError } from "@/lib/errorMessages";

const BASE = "/api/v2/outreach/communications";
const MESSAGE_LOG_BASE = "/api/v2/outreach/message-log";

// The SDK for both outreach communication surfaces.
//
// The thread methods are the candidate email tab's, and are application-scoped
// throughout: each one names the application it is acting for, because the tab
// always knows one and the Communication Center — which uses the
// candidate-scoped routes in the same namespace — never does.
//
// The message-log pair below is the Communication Center's Table view, and is
// scoped to nothing: it is the whole log, filtered by channel and search. It
// lives here rather than on InboxService because a component test mocks the
// page's own domain service, so every call the page makes has to be one of
// these.
export const CommunicationsService = Object.freeze({
  listThreads: async (candidateId, { applicationId } = {}) =>
    (
      await apiService.getSecure(`${BASE}/candidates/${candidateId}/threads`, {
        params: { application_id: applicationId },
      })
    ).data,

  getThread: async (candidateId, threadId, { applicationId } = {}) =>
    (
      await apiService.getSecure(`${BASE}/candidates/${candidateId}/threads/${threadId}`, {
        params: { application_id: applicationId },
      })
    ).data,

  // `threadId` omitted starts a new conversation: the backend mints one. The
  // key is left off the payload entirely rather than sent as null, so "start a
  // new one" and "add to this one" are distinguishable on the wire.
  send: async ({ candidateId, applicationId, threadId, subject, body }) =>
    (
      await apiService.postSecure(`${BASE}/send`, {
        candidate_id: candidateId,
        channel: "email",
        application_id: applicationId,
        ...(threadId ? { thread_id: threadId } : {}),
        subject,
        body,
      })
    ).data,

  // Flat table of every message, outbound sends and inbound replies together,
  // filterable by channel and by the search box — backs the Table view.
  listMessageLog: async ({ page = 1, pageSize = 50, channel, search } = {}) => {
    const params = { page, page_size: pageSize };
    if (channel) params.channel = channel;
    if (search) params.search = search;
    const r = await apiService.getSecure(MESSAGE_LOG_BASE, { params });
    return r.data;
  },
  // Downloads the message log as a CSV file. `channel` omitted exports every
  // channel together; a specific channel exports just that one.
  exportMessageLog: async ({ channel, search } = {}) => {
    const params = {};
    if (channel) params.channel = channel;
    if (search) params.search = search;
    const response = await apiService.getSecure(`${MESSAGE_LOG_BASE}/export`, {
      params,
      responseType: "blob",
    });

    const blob = response.data instanceof Blob ? response.data : new Blob([response.data], { type: response.headers?.["content-type"] });

    // A refusal can arrive as a JSON body on a 200 blob request. It is raised
    // with the same `userMessage` the interceptor puts on every rejection,
    // chosen from its error_code, so the caller shows one kind of error.
    if (blob.type?.includes("application/json")) {
      let errorData = {};
      try {
        errorData = JSON.parse(await blob.text());
      } catch {
        /* not JSON after all — the generic sentence below stands */
      }
      const error = new Error("Export refused");
      error.response = { data: errorData };
      error.userMessage = messageForError(error, "We couldn't export messages. Try again.");
      throw error;
    }

    const contentDisposition = response?.headers?.["content-disposition"] || "";
    const filenameMatch = contentDisposition.match(/filename\*?=(?:UTF-8''|")?([^";]+)/i);
    const filename = filenameMatch ? decodeURIComponent(filenameMatch[1].replace(/"/g, "")) : "outreach-messages.csv";
    const truncated = response?.headers?.["x-export-truncated"] === "true";

    const url = URL.createObjectURL(blob);
    try {
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } finally {
      // Revoked on the next tick, not this one: revoking in the tick that
      // clicked the link cancels the download in some browsers.
      setTimeout(() => URL.revokeObjectURL(url), 0);
    }
    return { filename, truncated };
  },

  // AI help with the message being composed, on both screens: write it from
  // the recruiter's `instruction`, or improve the `subject`/`body` they wrote.
  // `threadId` names the email conversation a reply answers. Only the keys
  // the caller has are sent.
  draft: async ({ candidateId, channel = "email", applicationId, threadId, instruction, subject, body }) =>
    (
      await apiService.postSecure(`${BASE}/candidates/${candidateId}/draft`, {
        channel,
        ...(applicationId ? { application_id: applicationId } : {}),
        ...(threadId ? { thread_id: threadId } : {}),
        ...(instruction ? { instruction } : {}),
        ...(subject ? { subject } : {}),
        ...(body ? { body } : {}),
      })
    ).data,

  // ── Communication Center (Chat view) ─────────────────────────────────────
  // Candidate-scoped, across every channel. `quiet` marks a background poll:
  // its failure must not raise the permission toast on every tick.

  listConversations: async ({ channel, search, quiet = false } = {}) => {
    const params = {};
    if (channel) params.channel = channel;
    if (search) params.search = search;
    return (await apiService.getSecure(`${BASE}/conversations`, { params, skipPermissionToast: quiet })).data;
  },

  getConversation: async (candidateId, { channel, quiet = false } = {}) => {
    const params = {};
    if (channel) params.channel = channel;
    return (await apiService.getSecure(`${BASE}/conversations/${candidateId}`, { params, skipPermissionToast: quiet })).data;
  },

  // An absent channel clears every channel's unread, a named one only its own.
  markConversationRead: async (candidateId, { channel } = {}) =>
    (await apiService.postSecure(`${BASE}/conversations/${candidateId}/read`, channel ? { channel } : {}, { skipPermissionToast: true })).data,

  getTimeline: async (candidateId) => (await apiService.getSecure(`${BASE}/conversations/${candidateId}/timeline`)).data,

  // Any channel. A quote rides along only when one is staged; the subject only
  // when the caller has one (the server ignores it off email).
  sendMessage: async ({ candidateId, channel, body, subject, quotedMessageId, quotedReplyId }) =>
    (
      await apiService.postSecure(`${BASE}/send`, {
        candidate_id: candidateId,
        channel,
        body,
        ...(subject ? { subject } : {}),
        ...(quotedMessageId ? { quoted_message_id: quotedMessageId } : {}),
        ...(quotedReplyId ? { quoted_reply_id: quotedReplyId } : {}),
      })
    ).data,

  // A bot can never message someone first, so a candidate's Telegram opens
  // only after they follow a link. This emails them that link; the email lands
  // in the candidate's Email channel like any other.
  sendTelegramInvite: async (candidateId, { applicationId } = {}) =>
    (await apiService.postSecure(`${BASE}/candidates/${candidateId}/telegram-invite`, { application_id: applicationId ?? null })).data,
});

export default CommunicationsService;
